import React, { useState } from "react";
import { Copy, Check, Quote } from "lucide-react";

/**
 * "Cite this page" widget — one-click citation copy in APA, MLA, and BibTeX.
 * Standard on university/research sites. Nobody else in the peptide space
 * has this; adding it signals editorial authority to Google + AI engines and
 * makes it trivial for journalists / bloggers to cite the page correctly.
 *
 * Props:
 *   title      — human-readable page title
 *   url        — canonical URL of the page
 *   publishedDate — ISO date string (e.g., "2026-05-26")
 *   author     — editorial byline (default: "PeptidesForMedSpas Editorial Team")
 */
const SITE = "PeptidesForMedSpas";
const SITE_URL = "https://www.peptidesformedspas.org";

const buildCitations = ({ title, url, publishedDate, author }) => {
  const d = publishedDate ? new Date(publishedDate) : new Date();
  const year = d.getFullYear();
  const monthName = d.toLocaleString("en-US", { month: "long" });
  const day = d.getDate();
  const accessedDate = new Date().toLocaleString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });
  const authorName = author || `${SITE} Editorial Team`;
  return {
    apa: `${authorName} (${year}, ${monthName} ${day}). ${title}. ${SITE}. Retrieved ${accessedDate}, from ${url}`,
    mla: `${authorName}. "${title}." ${SITE}, ${day} ${monthName.slice(0, 3)} ${year}, ${url}. Accessed ${accessedDate}.`,
    chicago: `${authorName}. "${title}." ${SITE}. ${monthName} ${day}, ${year}. ${url}.`,
    bibtex: `@misc{peptidesformedspas${year},
  author       = {{${authorName}}},
  title        = {${title}},
  howpublished = {${SITE}, \\url{${url}}},
  year         = {${year}},
  month        = {${monthName.toLowerCase().slice(0, 3)}},
  note         = {Accessed: ${accessedDate}}
}`,
  };
};

export const CiteThisPage = ({ title, url = SITE_URL, publishedDate, author }) => {
  const [format, setFormat] = useState("apa");
  const [copied, setCopied] = useState(false);
  const citations = buildCitations({ title, url, publishedDate, author });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(citations[format]);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_) {
      /* clipboard unavailable — no user notification needed for a copy convenience */
    }
  };

  const formats = [
    { key: "apa", label: "APA" },
    { key: "mla", label: "MLA" },
    { key: "chicago", label: "Chicago" },
    { key: "bibtex", label: "BibTeX" },
  ];

  return (
    <aside
      className="border-2 border-[#f0d8d4] bg-white rounded-2xl p-5 md:p-6 my-8"
      data-testid="cite-this-page"
      aria-label="Cite this page"
    >
      <div className="flex items-center gap-2 mb-3">
        <Quote className="w-5 h-5 text-[#b89a6d]" />
        <h3 className="text-base md:text-lg font-bold text-[#3a2a26]">Cite this page</h3>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3" role="tablist" aria-label="Citation format">
        {formats.map((f) => (
          <button
            key={f.key}
            role="tab"
            aria-selected={format === f.key}
            onClick={() => setFormat(f.key)}
            data-testid={`cite-format-${f.key}`}
            className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${
              format === f.key
                ? "bg-[#d4a5ad] text-white"
                : "bg-[#fffaf5] text-[#5a4a40] border border-[#f0d8d4] hover:border-[#d4a5ad]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <pre
        className="text-xs md:text-sm text-[#5a4a40] bg-[#fffaf5] border border-[#f0d8d4] rounded-lg p-3 whitespace-pre-wrap break-words font-mono leading-relaxed"
        data-testid="cite-output"
      >
        {citations[format]}
      </pre>
      <button
        type="button"
        onClick={copy}
        data-testid="cite-copy-btn"
        className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-[#d4a5ad] hover:bg-[#c4949c] text-white text-sm font-semibold rounded-lg transition-colors"
      >
        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        {copied ? "Copied" : "Copy citation"}
      </button>
      <p className="text-xs text-[#a09186] mt-3 italic">
        Free to cite with attribution. Content is educational and research-focused; not medical advice.
      </p>
    </aside>
  );
};

export default CiteThisPage;
